import type {
  InventoryVehicle,
  InventoryFilters,
  InventoryTableRow,
} from '../../api/services/lupp/inventory/dto.inventory';
import type { ParsedFilterOptions } from '../../api/services/lupp/inventory/transformers.inventory';
import type { FilterState, TableColumn } from '@lucid/lego';
import {
  AVAILABILITY_OPTIONS,
  AIR_MODEL_YEAR_OPTIONS,
  GRAVITY_MODEL_YEAR_OPTIONS,
  getTrimOptionsForModel,
} from '../../api/services/lupp/inventory/filterOptions.inventory';
import type { ImageProps } from '@lucid/lego';
import { formatCurrency } from '../../utils/tools';

// Re-export InventoryTableRow for consumers that import from here
export type { InventoryTableRow };

/**
 * Creates an empty InventoryTableRow with all fields defaulted.
 * Use with partial overrides for placeholder rows (e.g., URL deep-link, shortcode checkout).
 * @param overrides - Fields to override on the empty row
 * @returns A complete InventoryTableRow
 */
export const createEmptyVehicleRow = (
  overrides: Partial<InventoryTableRow> = {}
): InventoryTableRow => ({
  vin: '',
  webLink: '',
  batchNumber: '',
  availability: '',
  locationName: '',
  city: '',
  country: '',
  model: '',
  trim: '',
  drivetrain: '',
  exteriorColor: '',
  exteriorColorCode: '',
  interiorColor: '',
  interiorColorCode: '',
  appearance: '',
  appearanceCode: '',
  roof: '',
  wheels: '',
  wheelsCode: '',
  year: '',
  adas: '',
  adasCode: '',
  soundSystem: '',
  soundSystemCode: '',
  frontSeats: '',
  frontSeatsCode: '',
  rearSeats: '',
  comfortPackage: '',
  packages: '',
  packagesCode: '',
  dynamicHandlingPackage: '',
  technologyPackage: '',
  towingPackage: '',
  uuid: '',
  shortCode: '',
  condition: 'new',
  ...overrides,
});

/**
 * Convert a simple image URL to ImageProps format for swatch filters
 */
const urlToImageProps = (url: string, alt: string): ImageProps => ({
  uri_desktop: { '1x': url, '2x': url },
  uri_mobile: { '1x': url, '2x': url },
  alt,
});

/**
 * Filter options based on selected trims availability
 * Returns only options that are available for at least one of the selected trims
 */
const filterOptionsByTrims = <T extends { availableTrims?: string[] }>(
  options: T[],
  selectedTrims: string[] | undefined
): T[] => {
  // If no trims selected, show all options
  if (!selectedTrims || selectedTrims.length === 0) return options;

  // selectedTrims now contains codes directly (PURE, AT, GT)
  return options.filter((opt) => {
    // If option has no trim restrictions, it's available for all trims
    if (!opt.availableTrims || opt.availableTrims.length === 0) return true;
    // Check if option is available for any of the selected trims
    return opt.availableTrims.some((trim) => selectedTrims.includes(trim));
  });
};

/**
 * Labels type for i18n support
 */
export type InventoryLabels = Record<string, string> | undefined;

/**
 * Get model year filter options based on selected model
 * @param isGravity - Whether the Gravity model is selected
 * @returns Year filter options for the selected model
 */
const getModelYearOptions = (isGravity: boolean): { value: string; label: string }[] => {
  const options = isGravity ? GRAVITY_MODEL_YEAR_OPTIONS : AIR_MODEL_YEAR_OPTIONS;
  return options.map((opt) => ({ value: opt.value, label: opt.label }));
};

/**
 * Column definitions for the inventory table (base columns without dynamic filters)
 */
export const getInventoryColumns = (
  prCodes: ParsedFilterOptions | undefined,
  isGravity: boolean,
  selectedTrims?: string[],
  labels?: InventoryLabels,
  locale?: string
): TableColumn<InventoryTableRow>[] => [
  {
    key: 'vin',
    title: labels?.['configurator.table.vin'] ?? 'VIN',
    width: '180px',
    sortable: true,
  },
  {
    key: 'webLink',
    title: labels?.['configurator.table.webLink'] ?? 'Web Link',
    width: '100px',
    sortable: false,
  },
  {
    key: 'batchNumber',
    title: labels?.['configurator.table.batchNumber'] ?? 'Batch #',
    width: '120px',
    sortable: true,
  },
  // Filters ordered based on design: Model, Condition, Trim, Availability, Exterior, Interior, Wheels, Appearance, Driver Assistance, Sound System, Seating, Packages
  {
    key: 'model',
    title: labels?.['configurator.column.model'] ?? 'Model',
    width: '100px',
    sortable: true,
    filter: {
      type: 'radio',
      // Model options are always Air/Gravity - not from prCodes which includes bundle types
      options: [
        { value: 'AIR', label: labels?.['util.air'] ?? 'Air' },
        { value: 'GRAVITY', label: labels?.['util.gravity'] ?? 'Gravity' },
      ],
    },
  },
  {
    key: 'condition',
    title: labels?.['configurator.column.condition'] ?? 'Condition',
    width: '120px',
    sortable: true,
  },
  {
    key: 'year',
    title: labels?.['configurator.column.modelYear'] ?? 'Model Year',
    width: '120px',
    sortable: true,
    filter: {
      type: 'checkbox',
      options: getModelYearOptions(isGravity),
    },
  },
  {
    key: 'trim',
    title: labels?.['configurator.column.trim'] ?? 'Trim',
    width: '120px',
    sortable: true,
    filter: {
      type: 'checkbox',
      options: getTrimOptionsForModel(isGravity, prCodes).map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    },
  },
  {
    key: 'price',
    title: labels?.['configurator.column.price'] ?? 'Price',
    width: '140px',
    sortable: true,
    render: (value: unknown) =>
      value == null ? '—' : formatCurrency(value as number, locale ?? 'en-US'),
  },
  {
    key: 'availability',
    title: labels?.['configurator.column.availability'] ?? 'Availability',
    width: '140px',
    sortable: true,
    filter: {
      type: 'checkbox',
      options: AVAILABILITY_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
    },
  },
  {
    key: 'locationName',
    title: labels?.['configurator.column.locationName'] ?? 'Location Name',
    width: '160px',
    sortable: true,
  },
  {
    key: 'city',
    title: labels?.['configurator.column.city'] ?? 'Location City',
    width: '120px',
    sortable: true,
  },
  {
    key: 'country',
    title: labels?.['configurator.column.country'] ?? 'Location Country',
    width: '120px',
    sortable: true,
  },
  {
    key: 'drivetrain',
    title: labels?.['configurator.column.drivetrain'] ?? 'Drivetrain',
    width: '120px',
    sortable: true,
  },
  {
    key: 'exteriorColor',
    title: labels?.['configurator.column.exteriorColor'] ?? 'Exterior Color',
    width: '160px',
    sortable: true,
    filter: {
      type: 'swatch',
      swatchShape: 'circle',
      options: filterOptionsByTrims(prCodes?.exteriorColor || [], selectedTrims).map((opt) => ({
        value: opt.value,
        label: opt.label,
        image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
      })),
    },
  },
  {
    key: 'interiorColor',
    title: labels?.['configurator.column.interiorTheme'] ?? 'Interior Theme',
    width: '200px',
    sortable: true,
    filter: {
      type: 'swatch',
      swatchShape: 'rectangle',
      options: filterOptionsByTrims(prCodes?.interiorTheme || [], selectedTrims).map((opt) => ({
        value: opt.value,
        label: opt.label,
        image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
      })),
    },
  },
  {
    key: 'wheels',
    title: labels?.['configurator.column.wheels'] ?? 'Wheels',
    width: '180px',
    sortable: true,
    filter: {
      type: 'swatch',
      swatchShape: 'circle',
      options: filterOptionsByTrims(prCodes?.wheels || [], selectedTrims).map((opt) => ({
        value: opt.value,
        label: opt.label,
        image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
      })),
    },
  },
  {
    key: 'appearance',
    title: labels?.['configurator.column.appearance'] ?? 'Appearance',
    width: '180px',
    sortable: true,
    filter: {
      type: 'swatch',
      swatchShape: 'rectangle',
      options: filterOptionsByTrims(prCodes?.appearance || [], selectedTrims).map((opt) => ({
        value: opt.value,
        label: opt.label,
        image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
      })),
    },
  },
  {
    key: 'roof',
    title: labels?.['configurator.column.roof'] ?? 'Roof',
    width: '120px',
    sortable: true,
  },
  {
    key: 'adas',
    title: labels?.['configurator.column.driverAssistance'] ?? 'Driver Assistance',
    width: '160px',
    sortable: true,
    filter: {
      type: 'checkbox',
      options: (prCodes?.driverAssistance || []).map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    },
  },
  {
    key: 'soundSystem',
    title: labels?.['configurator.column.soundSystem'] ?? 'Sound System',
    width: '140px',
    sortable: true,
    filter: {
      type: 'checkbox',
      options: (prCodes?.soundSystem || []).map((opt) => ({ value: opt.value, label: opt.label })),
    },
  },
  {
    key: 'frontSeats',
    title: labels?.['configurator.column.seating'] ?? 'Seating',
    width: '280px',
    sortable: true,
    filter: {
      type: 'checkbox',
      options: (prCodes?.seating || []).map((opt) => ({ value: opt.value, label: opt.label })),
    },
  },
  {
    key: 'packages',
    title: labels?.['configurator.column.packages'] ?? 'Packages',
    width: '200px',
    sortable: true,
    filter: {
      type: 'checkbox',
      options: (prCodes?.packages || []).map((opt) => ({ value: opt.value, label: opt.label })),
    },
  },
  {
    key: 'rearSeats',
    title: labels?.['configurator.column.rearSeats'] ?? 'Rear Seats',
    width: '280px',
    sortable: true,
  },
  {
    key: 'comfortPackage',
    title: labels?.['configurator.column.comfortPackage'] ?? 'Comfort & Convenience Package',
    width: '260px',
    sortable: true,
  },
];

/**
 * Transform inventory vehicle data to table row format
 * @param vehicle - Raw inventory vehicle from API
 * @returns Formatted table row
 */
export const transformVehicleToRow = (vehicle: InventoryVehicle): InventoryTableRow => {
  // Determine condition based on category/subCategory
  let condition: 'new' | 'demo' | 'pre-owned' = 'new';
  if (vehicle.category === 'demo' || vehicle.subCategory === 'demo') {
    condition = 'demo';
  } else if (vehicle.category === 'pre-owned' || vehicle.subCategory === 'pre-owned') {
    condition = 'pre-owned';
  }

  return {
    vin: vehicle.VIN,
    webLink: vehicle.shortCode,
    batchNumber: vehicle.batchNumber,
    availability: vehicle.availability,
    locationName: vehicle.address?.locationName || '',
    city: vehicle.address?.city || '',
    country: vehicle.address?.country || '',
    model: vehicle.model?.technicalName || '',
    trim: vehicle.modelVariant?.technicalName || '',
    drivetrain: vehicle.motorDrive?.technicalName || '',
    exteriorColor: vehicle.color?.technicalName || '',
    exteriorColorCode: vehicle.color?.sapCode || '',
    interiorColor: vehicle.interior?.technicalName || '',
    interiorColorCode: vehicle.interior?.sapCode || '',
    appearance: vehicle.appearance?.technicalName || '',
    appearanceCode: vehicle.look?.sapCode || '',
    roof: vehicle.roof?.technicalName || '',
    wheels: vehicle.wheels?.technicalName || '',
    wheelsCode: vehicle.wheels?.sapCode || '',
    year: vehicle.modelYear,
    adas: vehicle.driverAssistance?.technicalName || '',
    adasCode: vehicle.driverAssistance?.sapCode || '',
    soundSystem: vehicle.soundSystem?.technicalName || '',
    soundSystemCode: vehicle.soundSystem?.sapCode || '',
    frontSeats: vehicle.seating?.technicalName || '',
    frontSeatsCode: vehicle.seating?.sapCode || '',
    rearSeats: vehicle.rearSeats?.backSeats || '',
    comfortPackage: vehicle.packages?.technicalName || '',
    packages: vehicle.packages?.technicalName || '',
    packagesCode: vehicle.packages?.sapCode || '',
    dynamicHandlingPackage: '',
    technologyPackage: '',
    towingPackage: '',
    // Additional fields for checkout detail request
    uuid: vehicle.uuid,
    shortCode: vehicle.shortCode,
    condition,
    // Pricing fields
    baseMsrp: vehicle.pricing?.baseMSRP,
    price: vehicle.pricing?.totalMSRP,
    destinationCharge: vehicle.pricing?.destinationCharge,
    finalPrice: vehicle.pricing?.finalPrice,
  };
};

/**
 * Transform array of inventory vehicles to table data
 * @param vehicles - Array of inventory vehicles from API
 * @returns Array of formatted table rows
 */
export const transformInventoryData = (vehicles: InventoryVehicle[]): InventoryTableRow[] => {
  return vehicles.map(transformVehicleToRow);
};

/**
 * Default filter state for the inventory table
 */
export const DEFAULT_FILTER_STATE: FilterState = {
  model: 'AIR',
};

/**
 * Convert Table FilterState to InventoryFilters for API calls
 * Maps column-based filters to API parameter format
 */
export const filterStateToInventoryFilters = (
  filterState: FilterState,
  prCodes: ParsedFilterOptions | undefined
): InventoryFilters => {
  const filters: InventoryFilters = {
    condition: 'new', // Default condition
  };

  // Model filter - convert display name to API value (single select radio)
  if (filterState.model && typeof filterState.model === 'string') {
    const modelName = filterState.model;
    let apiValue = modelName;
    if (modelName === 'Air') apiValue = 'AIR';
    if (modelName === 'Gravity') apiValue = 'GRAVITY';
    filters.model = [apiValue];
  }

  // Trim filter - convert display names to API values
  if (filterState.trim && Array.isArray(filterState.trim)) {
    filters.trim = (filterState.trim as string[]).map((name) => {
      if (name === 'Pure') return 'PURE';
      if (name === 'Touring') return 'AT';
      if (name === 'Grand Touring') return 'GT';
      return name;
    });
  }

  // Availability filter
  if (filterState.availability && Array.isArray(filterState.availability)) {
    filters.availability = filterState.availability as string[];
  }

  // For prCode-based filters, we need to convert display names back to sapCodes
  const findSapCode = (
    options: { label: string; value: string }[] | undefined,
    displayName: string
  ): string | undefined => {
    const option = options?.find((opt) => opt.label === displayName);
    return option?.value;
  };

  // Exterior Color
  if (filterState.exteriorColor && Array.isArray(filterState.exteriorColor)) {
    const sapCodes = (filterState.exteriorColor as string[])
      .map((name) => findSapCode(prCodes?.exteriorColor, name))
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters.exteriorColor = sapCodes;
  }

  // Interior Color
  if (filterState.interiorColor && Array.isArray(filterState.interiorColor)) {
    const sapCodes = (filterState.interiorColor as string[])
      .map((name) => findSapCode(prCodes?.interiorTheme, name))
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters.interiorTheme = sapCodes;
  }

  // Appearance
  if (filterState.appearance && Array.isArray(filterState.appearance)) {
    const sapCodes = (filterState.appearance as string[])
      .map((name) => findSapCode(prCodes?.appearance, name))
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters.appearance = sapCodes;
  }

  // Wheels
  if (filterState.wheels && Array.isArray(filterState.wheels)) {
    const sapCodes = (filterState.wheels as string[])
      .map((name) => findSapCode(prCodes?.wheels, name))
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters.wheels = sapCodes;
  }

  // ADAS / Driver Assistance
  if (filterState.adas && Array.isArray(filterState.adas)) {
    const sapCodes = (filterState.adas as string[])
      .map((name) => findSapCode(prCodes?.driverAssistance, name))
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters.driverAssistance = sapCodes;
  }

  // Sound System
  if (filterState.soundSystem && Array.isArray(filterState.soundSystem)) {
    const sapCodes = (filterState.soundSystem as string[])
      .map((name) => findSapCode(prCodes?.soundSystem, name))
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters.soundSystem = sapCodes;
  }

  // Front Seats / Seating
  if (filterState.frontSeats && Array.isArray(filterState.frontSeats)) {
    const sapCodes = (filterState.frontSeats as string[])
      .map((name) => findSapCode(prCodes?.seating, name))
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters.seating = sapCodes;
  }

  return filters;
};
